// The Sky is Written: a preview of the share image and the three share
// buttons. Share Image copies the image where the browser allows it and
// downloads it otherwise; Share Link copies the link to the play's share page
// (the Worker draws it from the play); Copy Text puts the spoiler-free summary
// on the clipboard.
import type { PlayLevel, PlayState } from '@wits/core';
import { useEffect, useRef, useState } from 'react';
import type { PlayRecord } from '../../../systems/api/types';
import type { PlayStats } from '../../../systems/play/playStats';
import { captionParts, shareText } from '../../../systems/play/shareText';
import { copyOrDownloadImage, copyText, copyTextWhenReady } from '../../../systems/share/clipboard';
import { shareLink } from '../../../systems/share/shareLink';
import { Button } from '../../components/Button';
import { IMAGE_HEIGHT, IMAGE_WIDTH, renderSkyImage } from './skyImage';

interface Props {
  level: PlayLevel;
  board: PlayState;
  play: PlayRecord;
  stats: PlayStats;
}

/** The last image drawn, so reopening the section shows it at once. */
let cached: { key: string; blob: Blob } | null = null;

const FEEDBACK_MS = 1800;

/** A button's label for a moment after it was tapped. */
type Feedback = { image?: string; link?: string; text?: string };

export function SharePanel({ level, board, play, stats }: Props) {
  const key = `${play.id}:${play.finishedAt}:${board.constellations.map((c) => c.word).join(',')}`;
  const [blob, setBlob] = useState<Blob | null>(cached?.key === key ? cached.blob : null);
  const [url, setUrl] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>({});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let live = true;
    if (cached?.key === key) {
      setBlob(cached.blob);
      return;
    }
    renderSkyImage({ level, board, date: play.date, caption: captionParts(stats) })
      .then((b) => {
        cached = { key, blob: b };
        if (live) setBlob(b);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // The key names everything the image is drawn from.
  }, [key]);

  useEffect(() => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (f: Feedback) => {
    setFeedback(f);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFeedback({}), FEEDBACK_MS);
  };

  const onShareImage = async () => {
    if (!blob) return;
    const result = await copyOrDownloadImage(blob, `written-in-the-stars-${play.date}-${play.variant}.png`);
    flash({ image: result === 'copied' ? 'Image copied' : result === 'downloaded' ? 'Image saved' : 'Not possible here' });
  };

  const onShareLink = async () => {
    try {
      const ok = await copyTextWhenReady(shareLink(play.id));
      flash({ link: ok ? 'Link copied' : 'Not possible here' });
    } catch {
      flash({ link: 'Try again' });
    }
  };

  const onCopyText = async () => {
    const ok = await copyText(shareText(stats, window.location.origin));
    flash({ text: ok ? 'Text copied' : 'Not possible here' });
  };

  // An archive play either crossed the rollover (started on its own night) or was opened from the archive.
  const crossedRollover = new Date(play.startedAt).toISOString().slice(0, 10) === play.date;
  const note =
    play.context === 'archive'
      ? crossedRollover
        ? 'Finished after the night rolled over: an archive play.'
        : 'A night from the archive, now written.'
      : 'Come back tomorrow night for a new sky.';

  return (
    <div className="share">
      <div className="share-preview" style={{ aspectRatio: `${IMAGE_WIDTH} / ${IMAGE_HEIGHT}` }}>
        {url ? <img src={url} alt="Your night sky with its constellations" /> : <span className="share-preview-wait quiet">Drawing your sky…</span>}
      </div>
      <div className="share-actions">
        <Button variant="primary" leadingIcon="image" onClick={() => void onShareImage()} disabled={!blob}>
          {feedback.image ?? 'Share Image'}
        </Button>
        <Button variant="ghost" leadingIcon="link" onClick={() => void onShareLink()}>
          {feedback.link ?? 'Share Link'}
        </Button>
        <Button variant="ghost" leadingIcon="copy" onClick={() => void onCopyText()}>
          {feedback.text ?? 'Copy Text'}
        </Button>
      </div>
      <p className="share-note quiet">{note}</p>
    </div>
  );
}
